<template>
  <div id="app" class="wrapper">
    <div>
      <Header>
        <LanguageField v-if="model.step === 1" />
        <Button
          v-if="model.step >= 5 && model.step < 21"
          @click="saveForLater"
          class="btn btn-md btn-outline-tertiary text-nowrap">
          Save & Come Back Later
        </Button>
      </Header>
      <transition name="fade" mode="out-in">
        <router-view
          :model="model"
          :errors="errors"
          @new-model="model = $event"
        ></router-view>
      </transition>
    </div>

    <Transition name="fade">
      <Loader
        v-if="!!model.loader"
        :loader-type="model.loader"
      ></Loader>
    </Transition>
    <Footer/>
  </div>
</template>

<script>
import { formatDateForKotlin } from 'union_kotlin';
import enums from '@/mixins/enums';
import persons from '@/mixins/persons';
import lead from '@/mixins/lead';
import attachmentValidations from '@/mixins/attachmentValidations';
import distributionChannels from '@/mixins/distributionChannels';
import routes from '@/router/routes';
import Loader from '@/components/Loader/Loader.vue';
import './directives';
import './filters';
import { MOST_RELEVANT_COUNTRIES } from '@/config';
import '@/assets/scss/styles.scss';
import Header from '@/components/Header/Header.vue';
import Footer from '@/components/Footer/Footer.vue';
import LanguageField from '@/components/LanguageField/LanguageField.vue';
import Button from '@/components/Button/Button.vue';

export default {
  name: 'App',
  mixins: [enums, persons, lead, attachmentValidations, distributionChannels],
  components: {
    Button,
    LanguageField,
    Loader,
    Footer,
    Header,
  },
  data() {
    return {
      model: new this.$kotlin.model.Model_init(),
      created: false,
      mounted: false,
      euCountries: ['56', '100', '191', '196', '203', '208', '233', '246', '250', '300', '528', '372', '440',
        '428', '442', '348', '470', '276', '616', '620', '40', '642', '705', '826', '724', '752', '380', '756',
        '352', '438', '578'],
    };
  },
  computed: {
    errors() {
      const { validateModelFnName, Validator } = this.$kotlin;
      const modelErrors = new Validator().__proto__[validateModelFnName](this.model).toJSON(); // eslint-disable-line
      return [...modelErrors, ...this.attachmentsErrors];
    },
    loaded() { return this.created && this.mounted; },
  },
  async created() {
    const { LocaleEnum } = this.enums;

    // Set current date
    this.model.now = formatDateForKotlin(new Date());
    this.model.isNew = true;

    this.model.logUUID = crypto.randomUUID();

    // VIP initialization
    this.distributionChannelsInitialization();

    this.$axios.post(
      `${process.env.VUE_APP_API_CONTEXT}/japi/IUzpApi/logMessage`,
      [window.location.href, this.model.logUUID],
    );

    this.$axios.post(
      `${process.env.VUE_APP_API_CONTEXT}/japi/IUzpApi/logBroker`,
      [this.model.broker, this.model.logUUID],
    );

    // Set app locale from env
    const locale = process.env.NODE_ENV === 'production' ? process.env.LOCALE : process.env.VUE_APP_I18N_FALLBACK_LOCALE;
    this.model.locale = LocaleEnum[locale.toUpperCase()];

    // Check if device is mobile or not
    const toMatch = [/Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i];
    const isTouchable = (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0));
    this.model.isMobile = toMatch.some((toMatchItem) => navigator.userAgent.match(toMatchItem)) && isTouchable;

    // All services
    const parallelResults = await Promise.allSettled([
      // Load lead if found query lead
      new Promise((resolve, reject) => {
        const routesSteps = routes
          .map((route) => route.meta?.step)
          .filter((step) => step);
        const { lead: queryLead, channel, cislo } = this.$route.query;

        if (queryLead && this.$route.meta.step < Math.max(...routesSteps)) {
          this.model.loading = 'LEAD_LOADING';
          this.loadLead(queryLead)
            .then(() => {
              resolve(true);
              if (channel === 'vip') {
                const { DistributionChannelEnum } = this.enums;
                this.model.distributionChannel = DistributionChannelEnum.VIP;
              }
              if (cislo) {
                this.model.broker.number = cislo;
              }
            })
            .catch(() => reject(new Error('lead')));
        } else resolve(true);
      }),
      // Load server time
      new Promise((resolve, reject) => {
        this.$axios
          .get('/directservices/japi/IServerTimeApi/getServerTimeInMillis')
          .then((res) => { resolve(res.data); })
          .catch(() => reject(new Error('getServerTimeInMillis')));
      }),
      // Get countries
      new Promise((resolve, reject) => {
        this.$axios
          .get(`/directservices/japi/ICodelistApi/getCountries?command=all&language=${locale}`)
          .then((res) => {
            resolve(res.data);
          })
          .catch(() => reject(new Error('countries')));
      }),
      // Get streets
      new Promise((resolve, reject) => {
        const req = {
          street: '',
          city: '',
          psc: '',
          command: 'streets',
        };
        this.$axios
          .post('/directservices/japi/ICodelistApi/getAddresses', [req])
          .then((res) => {
            resolve(res.data);
          })
          .catch(() => reject(new Error('streets')));
      }),
      // Get cities
      new Promise((resolve, reject) => {
        const req = {
          street: '',
          city: '',
          psc: '',
          command: 'cities',
        };
        this.$axios
          .post('/directservices/japi/ICodelistApi/getAddresses', [req])
          .then((res) => {
            resolve(res.data);
          })
          .catch(() => reject(new Error('cities')));
      }),
      // Get app configuration
      new Promise((resolve, reject) => {
        this.$axios
          .get('/uzp_api/japi/IUzpApi/configuration')
          .then((res) => { resolve(res.data); })
          .catch(() => reject(new Error('App configuration')));
      }),
    ]);

    const [leadResult, serverTimeResult, countriesResult, streetsResult, citiesResult, appConfigurationResult] = parallelResults;

    // App countries service result
    if (countriesResult.status === 'fulfilled') {
      this.$store.commit('SET_COUNTRIES', countriesResult.value
        .map((country) => {
          let { area, areaName } = country;
          const { value } = country;
          if (area === 'EU' && this.euCountries.indexOf(value) === -1) {
            area = 'SVET';
            areaName = 'Svet';
          }

          return {
            value,
            label: country.label,
            search: country.search,
            calcKey: country.calcKey,
            optgroup: country.optgroup,
            name$: country.value,
            area,
            areaName,
            skLabel: country.skLabel,
          };
        })
        .sort((countryA, countryB) => {
          let countryAIndex = MOST_RELEVANT_COUNTRIES.findIndex((countryCode) => countryCode === countryA.value);
          if (countryAIndex === -1) countryAIndex = MOST_RELEVANT_COUNTRIES.length;
          let countryBIndex = MOST_RELEVANT_COUNTRIES.findIndex((countryCode) => countryCode === countryB.value);
          if (countryBIndex === -1) countryBIndex = MOST_RELEVANT_COUNTRIES.length;
          if (countryAIndex > countryBIndex) return 1;
          if (countryAIndex < countryBIndex) return -1;
          return 0;
        }));
      // Set countries and nationalities for first person, only if it's not from Lead opened
      if (!this.model.saveForLaterLeadUuid) {
        this.setPersonCountries(this.insuredPersons[0]);
      }
    } else {
      this.$router.maxStep = 22;
      this.$router.push({ name: 'NotAvailablePage', query: this.$route.query });
    }

    // App cities service result
    if (citiesResult.status === 'fulfilled') {
      const mappedResults = citiesResult.value
        .map((value) => (
          {
            label: value.city,
            search: value.city.normalize('NFC'),
            value: value.city,
          }
        ));
      const finalResults = [];
      mappedResults.forEach((result) => {
        if (finalResults.filter((element) => element.value === result.value).length === 0) {
          if (result.value) {
            finalResults.push(result);
          }
        }
      });
      this.$store.commit('SET_CITIES', finalResults);
    } else {
      this.$router.maxStep = 22;
      this.$router.push({ name: 'NotAvailablePage', query: this.$route.query });
    }

    // App streets service result
    if (streetsResult.status === 'fulfilled') {
      const mappedResults = streetsResult.value
        .map((value) => (
          {
            label: value.street,
            search: value.street.normalize('NFC'),
            value: value.street,
          }
        ));
      const finalResults = [];
      mappedResults.forEach((result) => {
        if (finalResults.filter((element) => element.value === result.value).length === 0) {
          if (result.value) {
            finalResults.push(result);
          }
        }
      });
      finalResults.sort((a, b) => {
        const x = a.value.toLowerCase();
        const y = b.value.toLowerCase();
        if (x < y) { return -1; }
        if (x > y) { return 1; }
        return 0;
      });
      this.$store.commit('SET_STREETS', finalResults);
    } else {
      this.$router.maxStep = 22;
      this.$router.push({ name: 'NotAvailablePage', query: this.$route.query });
    }

    // Lead service result
    if (leadResult.status !== 'fulfilled') {
      this.$router.maxStep = 22;
      this.$router.push({ name: 'NotAvailablePage', query: this.$route.query });
    }

    // Server time service result
    if (serverTimeResult.status === 'fulfilled') {
      this.model.now = formatDateForKotlin(new Date(serverTimeResult.value));
    }

    // App configuration service result
    if (appConfigurationResult.status === 'fulfilled') {
      this.$store.commit('SET_APP_CONFIGURATION', appConfigurationResult.value);
    } else {
      this.$router.maxStep = 22;
      this.$router.push({ name: 'NotAvailablePage', query: this.$route.query });
    }

    this.created = true;
  },
  mounted() {
    this.mounted = true;
  },
  watch: {
    loaded(loaded) {
      if (loaded && this.$route.name !== 'ESignatureThankYou') this.model.loader = null;
    },
  },
  methods: {
    async saveForLater() {
      const { query, params } = this.$route;
      const { LoaderEnum } = this.enums;
      this.model.loader = LoaderEnum.SAVING;
      this.$router.maxStep = 22;
      try {
        await this.saveLead(this.$route.meta?.step || 4, 'UZP', true);
        this.$router.push({ name: 'ThankYouSaveForLater', query, params });
      } catch (e) {
        this.$router.push({ name: 'NotAvailablePage', query, params });
      } finally {
        this.model.loader = null;
      }
    },
  },
};
</script>
<style lang="scss">
@import "src/assets/scss/global/variables";

.validation-output {
  display: none;
}

.cb-card .cb-content .validation-output {
  display: none;
}

.cb-card .cb-content {
  .input-dirty {
    .validation-output {
      display: block;
    }
  }
}

.cb-card .cb-content .cb-content .validation-output {
  display: none;
}

.cb-card .cb-content .cb-content .input-dirty {
  .validation-output {
    display: block;
  }
}

.rd-card .rd-content .validation-output {
  display: none;
}

.rd-card .rd-content .input-dirty {
  .validation-output {
    display: block;
  }
}

.input-dirty {
  .validation-output {
    display: block;
  }

  &.has-error .tf-container .tf .tfi {
    border: 1px solid $danger;
  }

  &.has-error .sf .sfi-container .sfi {
    border: 1px solid $danger;
  }

  &.has-error .df-container .dfi {
    border: 1px solid $danger;
  }

  &.has-error .rd-card {
    border: 1px solid $danger !important;
  }

  &.has-error .rd-card .rd-check:not(.rd-content .rd-check) {
    border: 1px solid $danger !important;
  }

  &.has-error .rd-container .rdi .rd-check {
    border: 1px solid $danger !important;
  }

  &.has-error .rd-disabled .rdi .rd-check {
    border: 1px solid $gray-300 !important;
  }

  &.has-error .cb-card {
    border: 1px solid $danger !important;
  }

  &.has-error .cb-card .cb-check:not(.cb-content .cb-check) {
    border: 1px solid $danger !important;
  }

  &.has-error .cb-container .cbi .cb-check {
    border: 1px solid $danger !important;
  }
}

.out-form-error {
  .rd-card {
    border: 1px solid $danger !important;
  }

  .rd-card .rd-check:not(.rd-content .rd-check) {
    border: 1px solid $danger !important;
  }

  .rd-container .rdi .rd-check {
    border: 1px solid $danger !important;
  }

  .cb-container .cbi .cb-check {
    border: 1px solid $danger !important;
  }
}

a.cursor-pointer {
  cursor: pointer;
}
</style>
